import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import {
  BrandMark,
  MODE_SHELL_EXPANDED_QUERY,
  ModeShell,
  type PhoneSheetAction,
} from "@bw/shared/components";
import { useAuth, useNotificationUnreadCount } from "@bw/shared/hooks";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";

import {
  FacilitySelector,
  useFacilitySelectorAvailable,
} from "@/components/facility/FacilitySelector";
import { NavUser } from "@/components/nav-user";
import { OrganizationSelector } from "@/components/organization/OrganizationSelector";
import { businessNavigation } from "@/config/modes";
import { useMyOrg } from "@/hooks/use-my-org";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { getQueueStats } from "@/libs/queueApi";

import { useBusinessNavAccess } from "./useBusinessNavAccess";

function ownerStatusLabel(status: string | undefined): string | null {
  switch (status) {
    case "pending":
      return "Draft";
    case "in_review":
      return "In review";
    case "rejected":
      return "Needs attention";
    case "suspended":
      return "Suspended";
    default:
      return null;
  }
}

/** Honest waiting count from the existing queue-stats endpoint. */
export function QueueNavBadge() {
  const { facilityId } = useResolvedFacility();
  const { data } = useQuery({
    queryKey: ["queue-nav-count", facilityId],
    queryFn: () => getQueueStats(facilityId as string),
    enabled: Boolean(facilityId),
    refetchInterval: 60_000,
    staleTime: 60_000,
  });
  const count = data?.queue_size ?? 0;
  if (count <= 0) return null;

  return (
    <Box
      component="span"
      aria-label={`${count} waiting`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 16,
        height: 16,
        px: 0.625,
        borderRadius: "8px",
        bgcolor: "action.hover",
        color: "text.secondary",
        fontSize: 10.5,
        fontWeight: 700,
      }}
    >
      {count > 99 ? "99+" : count}
    </Box>
  );
}

function NotificationAction() {
  const unreadCount = useNotificationUnreadCount("business");
  const label =
    unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications";

  return (
    <Tooltip title="Notifications">
      <IconButton
        component={Link}
        to="/notifications"
        aria-label={label}
        size="small"
        sx={{ color: "text.secondary" }}
      >
        <Badge variant="dot" color="warning" invisible={unreadCount <= 0} overlap="circular">
          <NotificationsOutlinedIcon sx={{ fontSize: 18 }} aria-hidden="true" />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}

function Brand({
  destination,
  facilityTriggerRef,
}: {
  destination: string | null;
  facilityTriggerRef: { current: HTMLButtonElement | null };
}) {
  const view = useMyOrg();
  // Phone (14d): the mode pill leads, so the bar shows only a plain facility name.
  const expanded = useMediaQuery(MODE_SHELL_EXPANDED_QUERY);
  const statusLabel = ownerStatusLabel(view.org?.status);
  const mark = <BrandMark aria-hidden="true" size="bar" />;

  if (!expanded) {
    return <FacilitySelector variant="plain" triggerRef={facilityTriggerRef} />;
  }

  return (
    <>
      {destination ? (
        <ButtonBase
          component={Link}
          to={destination}
          aria-label="Badminton Warriors Business home"
          sx={{ borderRadius: 1, flexShrink: 0 }}
        >
          {mark}
        </ButtonBase>
      ) : (
        <Box aria-label="Badminton Warriors Business" sx={{ display: "flex", flexShrink: 0 }}>
          {mark}
        </Box>
      )}
      <OrganizationSelector />
      <FacilitySelector />
      {statusLabel ? (
        <Chip size="small" label={statusLabel} sx={{ flexShrink: 0 }} />
      ) : null}
    </>
  );
}

/** The business modes shell: one definition drives tabs, sub-menus and search. */
export function BusinessShell({ children }: { children: ReactNode }) {
  const { allow, ready, storageKey, landing } = useBusinessNavAccess();
  const { user } = useAuth();
  const facilityTriggerRef = useRef<HTMLButtonElement | null>(null);
  const canSwitchFacility = useFacilitySelectorAvailable();
  // Phone sheet footer (14d): Switch facility opens the bar's facility selector.
  const phoneSheetActions = useMemo<PhoneSheetAction[]>(
    () =>
      canSwitchFacility
        ? [
            {
              id: "switch-facility",
              label: "Switch facility",
              icon: SwapHorizOutlinedIcon,
              onSelect: () => facilityTriggerRef.current?.click(),
            },
          ]
        : [],
    [canSwitchFacility],
  );
  const organization = useMyOrg().org;
  // Owners see "Owner · club". Owner details reach only owners and co-owners,
  // so a member without them is staff ("Staff · club"); no self-scoped API
  // returns a staff member's role names yet.
  const ownerUserId = organization?.owner_user_id;
  const organizationLabel = organization?.business_name
    ? ownerUserId && ownerUserId === user?.uid
      ? `Owner · ${organization.business_name}`
      : ownerUserId
        ? organization.business_name
        : `Staff · ${organization.business_name}`
    : null;

  return (
    <ModeShell
      definition={businessNavigation}
      allow={allow}
      ready={ready}
      storageKey={storageKey}
      brand={<Brand destination={landing} facilityTriggerRef={facilityTriggerRef} />}
      phoneSheetActions={phoneSheetActions}
      homePath={landing}
      trailing={<NotificationAction />}
      userMenuPages={(pages) => (
        <NavUser pages={pages} organizationLabel={organizationLabel} />
      )}
      badge={(pageId) => (pageId === "drop-in-live" ? <QueueNavBadge /> : null)}
      mainSx={{ py: { xs: 2, sm: 2.5 } }}
    >
      {children}
    </ModeShell>
  );
}
